import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { Box, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { ClearIcon } from "@mui/x-date-pickers";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FONT_SIZE, INK, INK_BLACK } from "../../../../theme/tokens";
import type { SortingState } from "../../../../utils/multiColumnSorting";
import { DraggableList } from "../../DnD/DraggableList";
import { SystemDropdown } from "../../Inputs/SystemDropDown";
import SystemButton from "../../SystemButton/SystemButton";
import type { SystemSortingProps } from "./SystemSortingButtonWithPopup";
import { useSystemSortingOptionsContentState } from "./useSystemSortingOptionsContentState";

function makeStableIdFromState<T>(s: SortingState<T>, idx: number) {
	return `${String(s.sortColumns.join("|"))}__${s.sortOrder}__${idx}`;
}

/**
 * The key's position in the sort, shown once there is more than one.
 *
 * "Add Another Sort" appends, so a pick lands LAST and a table whose first key already orders every
 * row does not visibly move — which reads as the control having done nothing. The rows were
 * identical apart from their order, so there was nothing on screen to say the new key went to the
 * back of the queue. Numbering them says it, and makes the drag handle's purpose obvious at the
 * same time. Hidden tie-breakers are filtered out of this list and forced last in the state, so a
 * visible row's position IS its true priority.
 */
const SortPriorityBadge = ({ position }: { position: number }) => (
	<Box
		aria-hidden
		sx={{
			minWidth: BADGE.PILL_SIZE,
			height: BADGE.PILL_SIZE,
			borderRadius: BADGE.PILL_RADIUS_FULL,
			bgcolor: alpha(INK_BLACK, 0.06),
			color: INK.MUTED,
			fontSize: FONT_SIZE.LABEL_MICRO,
			fontWeight: 700,
			display: "grid",
			placeItems: "center",
			flexShrink: 0,
		}}
	>
		{position}
	</Box>
);

const SortingOrderElement = <T,>({ item, onClick, index }: { item: SortingState<T>; onClick: (e: React.MouseEvent) => void; index: number }) => {
	return (
		<Box
			key={makeStableIdFromState(item, index)}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
			}}
		>
			{item.label ?? item.sortColumns.join(", ")}{" "}
			<IconButton
				color={"inherit"}
				onClick={(event) => {
					onClick(event);
				}}
				size={"small"}
			>
				{item.sortOrder === "asc" ? <ArrowUpward fontSize={"small"} /> : item.sortOrder === "desc" ? <ArrowDownward fontSize={"small"} /> : "?"}
			</IconButton>
		</Box>
	);
};

export const SystemSortingOptionsContent = <T,>(props: SystemSortingProps<T>) => {
	const {
		sortingDropdownOptions,
		filteredLocalState,
		handleReorder,
		handleSortLabelClick,
		isItemClearable,
		handleClearClicked,
		handleRestoreDefaultClick,
		handleOptionToAddSelected,
	} = useSystemSortingOptionsContentState(props);

	const { t } = useTranslation();

	const dropdownContainerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLElement>(null);
	const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
		left: 0,
		top: 0,
	});
	const computeMenuPos = useCallback(() => {
		const container = dropdownContainerRef.current;
		const trigger = dropdownRef.current;
		if (!container || !trigger) return menuPos;

		const c = container.getBoundingClientRect();
		const t = trigger.getBoundingClientRect();

		const extraPadding = 24;

		return {
			top: t.bottom - c.top + extraPadding, // bottom of the select, relative to container
			left: t.left - c.left, // left aligned
		};
	}, [menuPos]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				height: "fit-content",
				minWidth: "350px",
			}}
			ref={dropdownContainerRef}
		>
			<DraggableList
				items={filteredLocalState}
				getId={makeStableIdFromState}
				onChange={handleReorder}
				renderItem={({ item, index, handleListeners, isDragging }) => (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							p: "6px 10px",
							borderRadius: 1.5,
							boxShadow: isDragging ? `0 0px 18px ${alpha(INK_BLACK, 0.35)}` : `0 0px 4px ${alpha(INK_BLACK, 0.15)}`,
							color: INK.SECONDARY,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							<IconButton size="small" aria-label="Drag row" {...handleListeners} color={"inherit"}>
								<DragIndicatorIcon fontSize="small" />
							</IconButton>
							{filteredLocalState.length > 1 && (
								<>
									<SortPriorityBadge position={index + 1} />
									<Box component="span" sx={visuallyHidden}>
										{t("system.sorting.row_description", {
											label: item.label ?? item.sortColumns.join(", "),
											position: index + 1,
											direction: t(`system.sorting.direction.${item.sortOrder}`),
										})}
									</Box>
								</>
							)}
							<SortingOrderElement
								item={item}
								index={index}
								onClick={(e) => {
									e.stopPropagation();
									handleSortLabelClick(item.label, item.sortColumns);
								}}
							/>
						</Box>

						{isItemClearable(item) && (
							<IconButton size={"small"} color={"inherit"} onClick={(event) => handleClearClicked(event, item)}>
								<ClearIcon fontSize={"small"} />
							</IconButton>
						)}
					</Box>
				)}
			/>
			<Box
				sx={{
					display: "flex",
					pt: 1,
					gap: 2,
				}}
			>
				<SystemDropdown
					disabled={sortingDropdownOptions.length === 0}
					fullWidth={true}
					sx={{
						minWidth: 140,
					}}
					label={t("system.sorting.addAnotherSort")}
					ref={dropdownRef}
					onClick={(e) => {
						/**
						 * Avoid the popup to close when clicking the dropdown
						 */
						e.stopPropagation();
						setMenuPos(computeMenuPos());
					}}
					onChange={handleOptionToAddSelected}
					MenuProps={{
						disablePortal: true,
						container: dropdownContainerRef.current,
						anchorReference: "anchorPosition",
						anchorPosition: menuPos,
					}}
					items={sortingDropdownOptions}
				/>
				<SystemButton buttonVariant={"secondary"} leadingIcon={<RefreshOutlined />} onClick={handleRestoreDefaultClick}>
					{t("system.sorting.restoreDefault")}
				</SystemButton>
			</Box>
		</Box>
	);
};
